import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BookingsConfigEnum, ServiceModel, ResourceModel, StatusModel, DataViewEnum, BookingDataModel,
  BookingModel, ColumnModel
} from '../models';
import { RootState } from '../app/store';
import { setFormattedData } from '../helpers/helper';

export interface BookingsState {
  currentBookingsConfig: BookingsConfigEnum;
  services: ServiceModel[];
  resources: ResourceModel[];
  statuses: StatusModel[];
  dataView: DataViewEnum;

  data: BookingDataModel;
}

const initialState: BookingsState = {
  currentBookingsConfig: BookingsConfigEnum.All,
  services: [],
  resources: [],
  statuses: [],
  dataView: DataViewEnum.List,

  data: {
    recordCount: 0,
    records: [],
    rawData: [],
    columns: []
  },
};

const uniqueDataKey = "bookingId";

const columnConfig: Record<string, Partial<ColumnModel>> = {
  primaryKeyId: {
      width: 60,
      formatter: (record: BookingModel) => record.bookingId
  },
  serviceName: {
    width: 160
  },
  customer: {
    width: 200,
    formatter: (record: BookingModel) => `${record.customerId} - ${record.customerFirstName} ${record.customerLastName}`
  },
  start: {
    width: 180,
    formatter: (record: BookingModel) => new Date(record.startDateTime).toLocaleString()
  },
  end: {
    width: 180,
    formatter: (record: BookingModel) => new Date(record.endDateTime).toLocaleString()
  },
  bookingDate: {
    width: 190,
    formatter: (record: BookingModel) => new Date(record.bookingDate).toLocaleString()
  },
  status: {
    width: 150,
    formatter: (record: BookingModel) => record.statusName
  },
  information: {
    width: 320,
    formatter: (record: BookingModel) => record.statusInformation
  },
  price: {
    width: 140,
    formatter: (record: BookingModel) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(record.price)
  }
};

export const businessSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    setBookingListData: (state, action: PayloadAction<{ services: ServiceModel[], resources: ResourceModel[], statuses: StatusModel[] }>) => {
      state.services = action.payload.services || [];
      state.resources = action.payload.resources || [];
      state.statuses = action.payload.statuses || [];
      return state;
    },
    goAll: (state) => {
      state.currentBookingsConfig = BookingsConfigEnum.All;
      return state;
    },
    goActive: (state) => {
      state.currentBookingsConfig = BookingsConfigEnum.Active;
      return state;
    },
    goCancelled: (state) => {
      state.currentBookingsConfig = BookingsConfigEnum.Cancelled;
      return state;
    },
    goCompleted: (state) => {
      state.currentBookingsConfig = BookingsConfigEnum.Completed;
      return state;
    },
    setDataView: (state, action: PayloadAction<DataViewEnum>) => {
      state.dataView = action.payload;
      return state;
    },
    setBookingData: (state, action: PayloadAction<{ recordCount: number, bookings: BookingModel[] }>) => {
      state.data = setFormattedData({ recordCount: action.payload ? action.payload.recordCount : undefined, rawData: action.payload ? action.payload.bookings : [] }, columnConfig, uniqueDataKey);
      return state;
    },
    removeBooking: (state, action: PayloadAction<number>) => {
      state.data.records = state.data.records.filter(record => record[uniqueDataKey] !== action.payload);
      state.data.rawData = state.data.rawData.filter(raw => raw[uniqueDataKey] !== action.payload);
      return state
    },
    setLogoutBookings: (state) => {
      state = initialState;
      return state;
    },
  },
});

export const { setBookingListData, goAll, goActive, goCancelled, goCompleted, setDataView, setBookingData, setLogoutBookings } = businessSlice.actions;

export const selectCurrentBookingsConfig = (state: RootState) =>(state.bookings.currentBookingsConfig);

export default businessSlice.reducer;